// ============================================================
// teamEdits - what each verb of the library's Teams view writes. Pure.
//
// A team lives on its members' sheets (engine/roster/team), so every verb
// is a list of member changes - this member now wears that record, or none -
// and the changes become one edit per ASSET: a character's sheet, or the
// people inside one stored scene file. The hook (useTeamActions) saves each
// edit as a new version on top of the version it was read from, and keeps
// `before` for the Undo.
//
// A scene is rewritten in its stored file JSON (`mapStoredPeople`), never
// decoded and encoded: reading a scene file re-mints its ids, and the
// members are keyed on the stored ones.
// ============================================================
import {
  normalTeamInfo,
  soleLeader,
  withoutBlock,
  withoutLead,
  sheetOnTeam,
  sideOf,
  sidePlan,
  splitPlan,
  teamInfoOf,
  type Livery,
  type TeamInfo,
} from '../roster/team';
import { mapStoredPeople, storedPeople } from '../scenes/sceneFile';
import { sameValue } from '../canonicalJson';
import { readTeam, type LibraryTeam, type TeamMember } from './teamIndex';
import type { KillTeamBlock } from '../killTeam/block';

/** One member's new record; null for no team. */
export interface MemberChange {
  id: string;
  team: TeamInfo | null;
}

/** What one asset's next version changes: the records by `'sheet'` (a character) or by cast id (a scene). */
export interface AssetEdit {
  assetId: string;
  kind: TeamMember['kind'];
  /** The version the records were read from: the save is refused if the asset has moved past it. */
  baseVersion: number;
  records: Map<string, TeamInfo | null>;
  /** The records as they were, for the Undo. */
  before: Map<string, TeamInfo | null>;
}

const SHEET = 'sheet';

// ── the verbs ────────────────────────────────────────────────

const eachMember = (team: LibraryTeam, next: (record: TeamInfo, m: TeamMember) => TeamInfo | null): MemberChange[] =>
  team.members.map((m) => ({ id: m.id, team: next(m.sheet.team, m) }));

export const renamePlan = (team: LibraryTeam, label: string): MemberChange[] =>
  eachMember(team, (r) => ({ ...r, label }));

export const recolourPlan = (team: LibraryTeam, livery: Livery): MemberChange[] =>
  eachMember(team, (r) => ({ ...r, livery }));

/** Beside another team's tag, or `''` for a side of its own - every member at once, anchors carrying their allies. */
export const allyPlan = (members: readonly TeamMember[], team: LibraryTeam, side: string): MemberChange[] =>
  sidePlan(members, team.tag, side);

export const movePlan = (ids: readonly string[], to: TeamInfo | null): MemberChange[] =>
  ids.map((id) => ({ id, team: to }));

/**
 * Delete: every member to another team, or to none (Merge is this with the other team as `to`). Teams that fought
 * beside the deleted one follow it - onto the side its members joined, or a side of their own - so no sheet is left
 * naming a side nobody holds. A target that was itself beside the deleted team becomes its own side's anchor.
 */
export function removePlan(members: readonly TeamMember[], team: LibraryTeam, to: LibraryTeam | null): MemberChange[] {
  const side = to && to.side !== team.tag ? to.side : (to?.tag ?? null);
  const target = to ? normalTeamInfo({ ...to.record, side: side ?? undefined }) : null;
  const allies = members
    .filter((m) => m.sheet.team.tag !== team.tag && sideOf(m.sheet.team) === team.tag)
    .map((m) => ({ id: m.id, team: normalTeamInfo({ ...m.sheet.team, side: side ?? undefined }) }));
  return [...eachMember(team, () => target), ...allies];
}

/**
 * One tag worn under several names made one team: the kept group - found by its name and colour, never by position,
 * so a list re-sorted since cannot pick another - with its colour, on every wearer of the tag. Its side when all its
 * members agree on one; else the tag's own, never one member's by the order they came in. Null when that group is no
 * longer there.
 */
export function unifyPlan(team: LibraryTeam, keep: { label: string; livery?: Livery }): MemberChange[] | null {
  const group = (team.conflict ?? []).find((g) => g.label === keep.label && (g.livery ?? '') === (keep.livery ?? ''));
  if (!group) return null;
  const sides = new Set(group.members.map((m) => sideOf(m.sheet.team)));
  const side = sides.size === 1 ? ([...sides][0] ?? team.tag) : team.tag;
  const record = teamInfoOf({
    tag: team.tag,
    label: group.label,
    ...(group.livery ? { livery: group.livery } : {}),
    side,
    // The kept group's kill-team, as its first member to carry one declares it.
    killTeam: group.members.find((m) => m.sheet.team.killTeam)?.sheet.team.killTeam,
  });
  const leader = soleLeader(group.members, team.members);
  return eachMember(team, (_, m) => (m.id === leader ? { ...record, leads: true } : record));
}

/**
 * The kill-team block on every member (plan modes-rework), each keeping his own leadership; null switches the team's
 * kill-team off, and with it everyone's leadership.
 */
export const killTeamPlan = (team: LibraryTeam, block: KillTeamBlock | null): MemberChange[] =>
  eachMember(team, (r) => spreadOnto(r, { tag: team.tag, block }, false));

/**
 * One PERSON leads the kill-team - his character and every copy of him inside a scene - and nobody else; null for
 * nobody. Null for a plan it refuses - a team that is no kill-team, a person not on it - so a stale pick never wipes
 * everyone's leadership in silence (the verb says which).
 */
export function leaderPlan(team: LibraryTeam, person: string | null): MemberChange[] | null {
  if (!team.killTeam || (person && !team.members.some((m) => m.person === person))) return null;
  return eachMember(team, (r, m) => (m.person === person ? { ...r, leads: true } : withoutLead(r)));
}

/** The character that was saved: its asset, and the person it is (his copies in scenes are him too). */
export interface SavedSheet {
  assetId: string;
  person: string;
}

/** What one sheet's save must carry to the rest of its team: a kill-team block edited on it, a lead it took. */
export interface Spread {
  tag: string;
  /** The block to write on everyone (null: switched off); absent when the save did not change it. */
  block?: KillTeamBlock | null;
  /** He took the lead: nobody else keeps it. */
  tookLead?: true;
}

/**
 * What saving a character's team record spreads (plan modes-rework): only what THIS edit changed on the same team - a
 * block edited here, a lead taken here. A sheet that joined, left, or merely carried a stale block spreads nothing, so
 * saving an old sheet can never switch the whole team off. Null for nothing to spread.
 */
export function spreadOf(before: TeamInfo | null | undefined, after: TeamInfo | null | undefined): Spread | null {
  if (!after?.tag || before?.tag !== after.tag) return null;
  const block = sameValue(before.killTeam ?? null, after.killTeam ?? null) ? {} : { block: after.killTeam ?? null };
  const lead = after.leads && !before.leads ? { tookLead: true as const } : {};
  return 'block' in block || lead.tookLead ? { tag: after.tag, ...block, ...lead } : null;
}

/**
 * One member's record with a spread applied: the block written (or the kill-team switched off, leadership and all),
 * and - when someone took the lead - the lead on him and off everyone else. `him`: this record is the saver's own (a
 * copy of him elsewhere). Only the block and the lead change: a side or a title set on a copy is kept.
 */
export function spreadOnto(record: TeamInfo, spread: Spread, him: boolean): TeamInfo {
  const withBlock =
    spread.block === undefined
      ? record
      : spread.block
        ? { ...record, killTeam: spread.block }
        : withoutLead(withoutBlock(record));
  if (!spread.tookLead) return withBlock;
  return him ? { ...withBlock, leads: true } : withoutLead(withBlock);
}

/** A sheet as the table holds it: its id (the same on a character and on his roster copies) and team. */
interface TableSheet {
  id: string;
  team?: TeamInfo;
}

/**
 * A save at the table spread over the rest of the team (plan modes-rework): the new team record of every roster member
 * wearing it - his own copies included, as `him` - and every OTHER session character wearing it; a record the spread
 * leaves as it was is left out (as `editsFor` leaves it out), so nothing is written for nothing. The caller writes them.
 */
export function tableSpread<S extends TableSheet>(
  spread: Spread,
  savedId: string,
  roster: readonly { id: string; sheet: S }[],
  library: readonly S[],
): { roster: { id: string; team: TeamInfo }[]; library: S[] } {
  const next = (sheet: S, him: boolean): TeamInfo | null => {
    if (sheet.team?.tag !== spread.tag) return null;
    const team = spreadOnto(sheet.team, spread, him);
    return sameValue(normal(sheet.team), normal(team)) ? null : team;
  };
  return {
    roster: roster.flatMap((c) => {
      const team = next(c.sheet, c.sheet.id === savedId);
      return team ? [{ id: c.id, team }] : [];
    }),
    library: library.flatMap((ch) => {
      const team = ch.id === savedId ? null : next(ch, false);
      return team ? [{ ...ch, team }] : [];
    }),
  };
}

/** The rest of the team with a spread applied: every member but the saved asset's; his copies in scenes are him. */
export function spreadPlan(
  team: LibraryTeam,
  spread: Spread,
  saved: { assetId: string; person: string },
): MemberChange[] {
  const others = { ...team, members: team.members.filter((m) => m.assetId !== saved.assetId) };
  return eachMember(others, (r, m) => spreadOnto(r, spread, m.person === saved.person));
}

/** Each name on a conflicting tag its own team, allied to the tag (`mint` supplies the new tags - gotcha 5). */
export const splitTeamPlan = (members: readonly TeamMember[], team: LibraryTeam, mint: (i: number) => string) =>
  splitPlan(members, team.tag, mint) as MemberChange[];

// ── from changes to edits ────────────────────────────────────

const normal = (r: TeamInfo | null) => (r ? normalTeamInfo(r) : null);
const keyOf = (m: TeamMember) => (m.kind === 'scene' ? m.castId : SHEET);

/**
 * One edit per asset with something to change, in the order its members come. The last change named for a member
 * wins; a change to what the member already means (records compared normalised) is left out. Refuses a member it
 * does not know.
 */
export function editsFor(members: readonly TeamMember[], changes: readonly MemberChange[]): AssetEdit[] {
  const byId = new Map(members.map((m) => [m.id, m]));
  const last = new Map(changes.map((c) => [c.id, c.team]));
  const edits = new Map<string, AssetEdit>();
  for (const [id, team] of last) {
    const m = byId.get(id);
    if (!m) throw new Error(`No library member ${id} to change.`);
    if (sameValue(normal(m.sheet.team), normal(team))) continue;
    const edit = edits.get(m.assetId) ?? newEdit(m);
    edit.records.set(keyOf(m), team);
    edit.before.set(keyOf(m), m.sheet.team);
    edits.set(m.assetId, edit);
  }
  return [...edits.values()];
}

const newEdit = (m: TeamMember): AssetEdit => ({
  assetId: m.assetId,
  kind: m.kind,
  baseVersion: m.version,
  records: new Map(),
  before: new Map(),
});

/** The Undo of an edit: the records put back, on top of the version the edit made. */
export const undoOf = (edit: AssetEdit, savedVersion: number): AssetEdit => ({
  ...edit,
  baseVersion: savedVersion,
  records: edit.before,
  before: edit.records,
});

/** A character's sheet with its edit applied. Refuses a scene's edit, or one with no record for the sheet. */
export function retagSheet<T extends { team?: TeamInfo }>(sheet: T, edit: AssetEdit): T {
  if (edit.kind !== 'character' || !edit.records.has(SHEET)) throw new Error('That edit is not for a character.');
  return sheetOnTeam(sheet, edit.records.get(SHEET) ?? null);
}

/**
 * A stored scene file with its edit applied to the named people, every other value shared with the input (never
 * mutated). Refuses, with a sentence, a file that is not a scene, or one in which someone named is no longer there
 * (the scene changed since it was read).
 */
export function retagSceneFile(file: unknown, edit: AssetEdit): unknown {
  const done = new Set<string>();
  const next = mapStoredPeople(file, (person) => {
    if (!edit.records.has(person.id)) return person;
    done.add(person.id);
    const sheet = (person.sheet ?? {}) as { team?: TeamInfo };
    return { ...person, sheet: sheetOnTeam(sheet, edit.records.get(person.id) ?? null) };
  });
  const gone = [...edit.records.keys()].filter((id) => !done.has(id));
  if (gone.length)
    throw new Error(`Someone in that scene is no longer there (${gone.join(', ')}); reload and try again.`);
  return next;
}

/** The records an edit names, as they stand in the content actually read (a re-read may differ from the index). */
function recordsIn(edit: AssetEdit, stored: unknown): Map<string, TeamInfo | null> {
  if (edit.kind === 'character') return new Map([[SHEET, readTeam((stored as { team?: unknown } | null)?.team)]]);
  const people = new Map(storedPeople(stored).map((p) => [p.id, readTeam((p.sheet as { team?: unknown })?.team)]));
  return new Map([...edit.records.keys()].map((k) => [k, people.get(k) ?? null]));
}

/**
 * An edit applied to the content actually read: the next version's content, every field but the named team records
 * as read. Refuses - with a sentence - when a named record is no longer what the edit was planned from (`before`,
 * compared normalised): a change made since (another tab, or the change just before this one) is never written over
 * with an old record. Refuses as `retagSheet` / `retagSceneFile` do, too.
 */
export function applyEdit(edit: AssetEdit, stored: unknown): unknown {
  for (const [key, now] of recordsIn(edit, stored)) {
    if (!sameValue(normal(now), normal(edit.before.get(key) ?? null))) {
      throw new Error('Its team changed since it was read; reload and try again.');
    }
  }
  return edit.kind === 'character' ? retagSheet(stored as { team?: TeamInfo }, edit) : retagSceneFile(stored, edit);
}
